import { Component, DestroyRef, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { otpHandler } from 'authentication/functions/otp-handler';
import { passwordPattern } from 'shared/validators/password-pattern-validator';
import { mustMatch } from 'shared/validators/must-match-validator';
import { filter, of, switchMap } from 'rxjs';
import { ModalService, StorageService, ToastService, UserApiService } from 'shared/services';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { UserCredentials } from 'shared/interfaces/user-credentials.interface';
import { PASSWORD_CHANGE_EMAIL } from 'shared/constants/general-constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-require-password-change',
    templateUrl: './required-password-change.component.html',
    styleUrls: ['./required-password-change.component.scss'],
    standalone: false,
})
export class RequiredPasswordChangeComponent implements OnInit {
    @Output() isLoggedIn = new EventEmitter<boolean>();

    public requestData!: any;
    public requirePasswordChangeForm = new UntypedFormGroup({
        password: new UntypedFormControl('', [Validators.required, Validators.minLength(8), passwordPattern()]),
        confirmPassword: new UntypedFormControl('', [Validators.required, mustMatch('password')]),
    });

    constructor(
        private userApiService: UserApiService,
        private authenticationService: AuthenticationService,
        private toastService: ToastService,
        private storageService: StorageService,
        private activatedRoute: ActivatedRoute,
        private modalService: ModalService,
        private router: Router,
        private destroyRef: DestroyRef,
    ) {}

    public ngOnInit(): void {
        this.activatedRoute.params
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((params) => {
                    if (params?.passwordChangeRequestId) {
                        return this.userApiService.getRequestData(params.passwordChangeRequestId);
                    }
                    return of(null);
                }),
            )
            .subscribe((requestData) => (this.requestData = requestData));

        this.requirePasswordChangeForm
            .get('password')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.requirePasswordChangeForm.get('confirmPassword')?.updateValueAndValidity());
    }

    public changePassword() {
        this.requirePasswordChangeForm.markAllAsTouched();
        this.requirePasswordChangeForm.updateValueAndValidity();

        if (this.requirePasswordChangeForm.valid) {
            const userCredentials = {
                email: this.storageService.getFromSession(PASSWORD_CHANGE_EMAIL) ?? this.requestData.email,
                password: this.requirePasswordChangeForm.value.password,
            } as UserCredentials;

            this.userApiService
                .changedPasswordUponRequest(this.requirePasswordChangeForm.value.password, this.requestData?.id)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    switchMap(() => {
                        this.toastService.showToast({
                            body: 'changePassword.changeSuccess',
                            severity: 'success',
                            ignoreRemoval: true,
                        });
                        return this.authenticationService.login(userCredentials);
                    }),
                    filter((response) => {
                        return otpHandler(response, userCredentials, this.toastService, this.modalService, this.router, this.destroyRef);
                    }),
                )
                .subscribe(() => this.isLoggedIn.emit(true));
        }
    }
}
